import React, { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Search, Upload, CheckCircle2, Link2, Link2Off, Loader2 } from 'lucide-react';

export default function AdminDocuments() {
  const [caseId, setCaseId] = useState('');
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [finalUrl, setFinalUrl] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const lookupCase = async () => {
    if (!caseId.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}`);
      setDoc(data);
    } catch (err) {
      toast.error('Case not found');
      setDoc(null);
    } finally {
      setLoading(false);
    }
  };

  const uploadFinal = async () => {
    if (!finalUrl.trim()) return;
    setActionLoading('upload');
    try {
      await api.post(`/admin/documents/${caseId}/upload-final`, { final_file_url: finalUrl });
      toast.success('Final document uploaded');
      setFinalUrl('');
      lookupCase();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setActionLoading(null);
    }
  };

  const approveDoc = async () => {
    setActionLoading('approve');
    try {
      await api.post(`/admin/documents/${caseId}/approve`, { approval_notes: 'Approved by admin' });
      toast.success('Document approved');
      lookupCase();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approval failed');
    } finally {
      setActionLoading(null);
    }
  };

  const releaseLink = async () => {
    setActionLoading('release');
    try {
      const { data } = await api.post(`/admin/documents/${caseId}/release-link`);
      toast.success(`Link released: ${data.link_url}`);
      lookupCase();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Release failed');
    } finally {
      setActionLoading(null);
    }
  };

  const revokeLink = async () => {
    setActionLoading('revoke');
    try {
      await api.post(`/admin/documents/${caseId}/revoke-link`);
      toast.success('Link revoked');
      lookupCase();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revoke failed');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Final Documents</h2>
        <p className="text-[#8696a0] text-sm mt-1">Upload, approve, and release final documents with secure links</p>
      </div>

      <div className="flex gap-2">
        <input value={caseId} onChange={e => setCaseId(e.target.value)}
          placeholder="Enter Case ID..."
          className="flex-1 bg-[#202c33] text-white border border-[#374045] rounded-lg px-3 py-2 text-sm" />
        <button onClick={lookupCase} disabled={loading}
          className="flex items-center gap-1 px-4 py-2 bg-[#00a884] text-white text-sm rounded-lg hover:bg-[#06cf9c] disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Lookup
        </button>
      </div>

      {doc && (
        <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5 space-y-4">
          <h3 className="text-white font-semibold">Case {doc.case_number || doc.id?.slice(0, 8)}</h3>
          <p className="text-[#8696a0] text-sm">Client: {doc.client_name || '—'} · Service: {doc.service_type || '—'} · Status: {doc.status}</p>

          <div className="border-t border-[#2a3942] pt-4 space-y-3">
            <label className="text-[#8696a0] text-xs block">Secure final document URL</label>
            <div className="flex gap-2">
              <input value={finalUrl} onChange={e => setFinalUrl(e.target.value)}
                placeholder="https://..."
                className="flex-1 bg-[#202c33] text-white border border-[#374045] rounded-lg px-3 py-2 text-sm font-mono" />
              <button onClick={uploadFinal} disabled={actionLoading === 'upload' || !finalUrl.trim()}
                className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                <Upload className="w-4 h-4" /> Upload
              </button>
            </div>

            <div className="flex gap-2 pt-2">
              <button onClick={approveDoc} disabled={actionLoading === 'approve'}
                className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50">
                <CheckCircle2 className="w-4 h-4" /> Approve Document
              </button>
              <button onClick={releaseLink} disabled={actionLoading === 'release'}
                className="flex items-center gap-1 px-3 py-2 bg-[#00a884] text-white text-sm rounded-lg hover:bg-[#06cf9c] disabled:opacity-50">
                <Link2 className="w-4 h-4" /> Release Link
              </button>
              <button onClick={revokeLink} disabled={actionLoading === 'revoke'}
                className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50">
                <Link2Off className="w-4 h-4" /> Revoke Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
